<template>
    <div v-if="isAuthenticated && filteredFeatureNames.length !== 0">
        <div class="content primary auth" v-if="filteredFeatureNames.includes('VINScanner')">
            <router-link :to="{ name: 'workorders', params: { url: '/workorders' }}" @click="(event: MouseEvent) => captureFeature(event, 'vinscanner')">
            <Card>
                <template #content>
                    <div class="item">
                        <div class="icon"><img src="../assets/img/icon-vinscanner2.svg" alt="VIN Scanner"></div>
                        <div class="content">
                            <div class="title"><h2>VIN Scanner</h2></div>
                            <div class="prose"><p>No more squinting and typing. Capture a picture of the VIN and save it to the work order - simple.</p></div>
                        </div>
                    </div>
                </template>
            </Card>
            </router-link>
        </div>

        <div class="content secondary auth" v-if="filteredFeatureNames.includes('DVI')">
            <router-link :to="{ name: 'dviworkorders', params: { url: '/dviworkorders' }}" @click="(event: MouseEvent) => captureFeature(event, 'dvi')">
            <Card>
                <template #content>
                    <div class="item">
                        <div class="icon"><img src="../assets/img/icon-dvi2.svg" alt="DVI"></div>
                        <div class="content">
                            <div class="title"><h2>Digital Vehicle Inspection</h2></div>
                            <div class="prose"><p>True mobile inspections. Add images and video to support all findings, and send to customers' devices.</p></div>
                        </div>
                    </div>
                </template>
            </Card>
            </router-link>
        </div>

        <div class="content tertiary auth" v-if="filteredFeatureNames.includes('Reporting')">
            <router-link :to="{ name: 'reporting', params: { url: '/reporting' }}" @click="(event: MouseEvent) => captureFeature(event, 'reporting')">
            <Card>
                <template #content>
                    <div class="item">
                        <div class="icon"><img src="../assets/img/icon-reporting.svg" alt="Reporting"></div>
                        <div class="content">
                            <div class="title"><h2>Reporting</h2></div>
                            <div class="prose"><p>Mobile reporting and dashboards for all of your core KPI's and metrics.</p></div>
                        </div>
                    </div>
                </template>
            </Card>
            </router-link>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useAuth0 } from "@auth0/auth0-vue";
import { ref, inject, onMounted, watch} from "vue";
import ApiService from "@/service/api.service";
import { openErrorModal } from '@/composables/useErrorLog';
import { useRouter } from 'vue-router';
import { useStore } from "vuex";

const posthog = inject("posthog");
const storeid = ref('');
const role = ref('');
const managedfeatureids = ref('');
const storestatus = ref('');
const allFeatures = ref([]);
const userRoles = ref([]);
const filteredFeatureNames = ref([]);
const featuresFetched = ref(false);
const { isAuthenticated, user, getAccessTokenSilently,isLoading } = useAuth0();
const router = useRouter();
const store = useStore();

onMounted(async () => {
  await listAllFeatures();
});
// watch isLoading and isAuthenticated object to handle auth0 successful login - for initial login
const stop = watch([isLoading, isAuthenticated], async ([loading, auth]) => {
  if (!loading && auth) {
      await listAllFeatures();
    stop();
  }
});
const listAllFeatures = async () => {
  if (isAuthenticated.value && !featuresFetched.value) {
    storeid.value = store.state.storeid;
    const accessToken = await getAccessTokenSilently();
    await ApiService.getStoreDetails(accessToken, storeid.value);    
    role.value = store.state.role;
    storestatus.value = store.state.storestatus;
    managedfeatureids.value = store.state.managedfeatureids;
    allFeatures.value = await getAllFeatures(accessToken);
    userRoles.value = await getAllUserRoles(accessToken);
    featuresFetched.value = true;

    if (storestatus.value == 'Disable') {
      return router.push('/storedisable');
    }

    if (storestatus.value == 'Enable' && managedfeatureids.value !== '') { 
      setFeatures();
    }
  }
  return;
}

/**
 * Filtering of features based on store features and user role
 */
const setFeatures = () => {
  const actualRole = userRoles.value.filter((item) => item.userrole === role.value)[0];

  // if the Store detail has no managedfeatureids then select all non-manageable feature in features table
  if (managedfeatureids.value === null) {
    // get all non-manageable features, it is accessible by default
    const filteredFeatures = allFeatures.value.filter((feature) => feature.manageable === false);
    // get only the feature names for simplicity
    filteredFeatureNames.value = filteredFeatures.map((item) => item.featurename);
    return;
  }
  // managedfeatureids exists in store details
  // if the Store detail has managedfeatureids but the role has no availablefeatureids, then use the managedfeatureids as features
  if (actualRole.availablefeatureids === null) {
    const storemanagedfeatureids = managedfeatureids.value.split(',').map(Number);
    const filteredFeatures = allFeatures.value.filter((feature) => storemanagedfeatureids.includes(feature.featureid) || feature.manageable === false);
    // get only the feature names for simplicity
    filteredFeatureNames.value = filteredFeatures.map((item) => item.featurename);
    return;
  }

  // if the Store detail has managedfeatureids and the role has availablefeatureids, return the matching ids in user role details and store details
  const roleavailablefeatureids = actualRole.availablefeatureids.split(',').map(Number);
  const storemanagedfeatureids = managedfeatureids.value.split(',').map(Number);

  // select only the matching ids between user role and store details
  const filteredfeatureids = roleavailablefeatureids.filter((item) => storemanagedfeatureids.includes(item));
  // select the feature items in feature table based on filteredfeatureids or non-manageable features, it is accessible by default
  const filteredFeatures = allFeatures.value.filter((feature) => filteredfeatureids.includes(feature.featureid) || feature.manageable === false);
  // get only the feature names for simplicity
  filteredFeatureNames.value = filteredFeatures.map((item) => item.featurename);
}

/**
 * API request for getting all feaures
 * @param accessToken 
 */
const getAllFeatures = async (accessToken: string) => {
	try {
		const response: Response = await fetch(process.env.VUE_APP_NODEJS_BASE_URL + "sa/features", {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				'Content-Type': 'application/json'
			},
		});
		const data = await response.json();
    if (response.status > 400) {
			throw new Error(JSON.stringify(data.error));
		}
		return data.message;
	} catch (e) {
    openErrorModal(e.message);
	}
}

/**
 * API request for getting all user roles
 * @param accessToken 
 */
const getAllUserRoles = async (accessToken: string) => {
	const url = process.env.VUE_APP_NODEJS_BASE_URL + "sa/userroles";
	try {
		const response: Response = await fetch(url, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				'Content-Type': 'application/json'
			},
		});
		const data = await response.json();
    if (response.status > 400) {
			throw new Error(JSON.stringify(data.error));
		}
		return data.message;
	} catch (e) {
		openErrorModal(e.message);
	}
}

const captureFeature = (event: MouseEvent, tabName: string) => {
  event.preventDefault();
  const email = user.value.email;
  posthog.capture(`${tabName}_tab_clicked`, { email: email });
}
</script>